<template>
  <div class="flex-1 flex flex-col justify-between mb-[400px]">
    <div class="divide-y divide-gray-200 dark:divide-dark-divider-border">
      <div>
        <h2 class="text-lg leading-6 font-medium text-gray-700 pb-6 dark:text-white">
          {{ group_type == "suggested" ? $t("Update Suggested List") : $t("Update Growth Matrix") }}
        </h2>
      </div>
      <Form :initial-values="initial_values" :validation-schema="schema" class="space-y-3" @submit="updateList">
        <div class="rounded-md shadow-sm -space-y-px">
          <TextInput
            label=""
            name="name"
            type="text"
            :placeholder="group_type == 'suggested' ? $t('Suggested list name') : $t('Growth matrix name')" />
        </div>
        <div class="rounded-md shadow-sm -space-y-px">
          <TextArea :value="suggestedList.description" label="" name="description" :placeholder="$t('Description')" />
        </div>
        <div
          :class="[!is_filter_valid ? 'border-red-400' : '']"
          class="p-2 rounded-md flex flex-col border-[1px] border-gray-300 dark:border-dark-divider-border">
          <div class="h-full pb-8" style="min-height: 8rem">
            <AdvancedMetricsFilterForm
              name="filter"
              :object-type="suggestedList.type"
              :initial-filter="suggestedList.filter"
              @update="
                ($event) => {
                  handleUpdateFilter($event)
                }
              " />
          </div>
          <p v-if="!is_filter_valid" class="inline text-[12px] float-left font-normal text-gray-500">
            {{ $t("Write a formula to indicate that the") }} {{ group_type == "suggested" ? $t("suggested list") : $t("growth matrix") }}
            {{ $t("is achieved") }}
          </p>
        </div>
        <div class="flex justify-end">
          <button type="submit" class="primary-btn w-24">{{ $t("Update") }}</button>
          <button type="button" class="cancel-btn w-24 ml-1" @click="$emit('cancel')">{{ $t("Cancel") }}</button>
        </div>
      </Form>
    </div>
  </div>
</template>

<script>
import * as Yup from "yup"
import TextInput from "@/components/shared/TextInput.vue"
import TextArea from "@/components/shared/TextArea.vue"
import { Form } from "vee-validate"
import dataService from "@/services/data.service"
import AdvancedMetricsFilterForm from "@/components/forms/AdvancedMetricsFilterForm.vue"
import { transformMetricFilterToServerFilter } from "@/services/data.transformers"
export default {
  components: {
    TextInput,
    Form,
    TextArea,
    AdvancedMetricsFilterForm
  },
  props: {
    suggestedList: {
      type: Object,
      required: true
    },
    metricsList: {
      type: Array,
      required: true
    }
  },
  emits: ["cancel", "currentItem", "done"],
  setup(props) {
    const schema = Yup.object().shape({
      name: Yup.string().required(),
      description: Yup.string(),
      group_type: Yup.string()
    })
    const initial_values = { ...props.suggestedList }

    return {
      schema,
      initial_values
    }
  },
  data() {
    return {
      is_filter_valid: true,
      group_type: null,
      current_filter: null,
      filter: this.suggestedList.filter
    }
  },
  created() {
    this.group_type = this.initial_values.group_type
    this.current_filter = this.$utils.clone(this.initial_values.filter)
  },
  methods: {
    handleUpdateFilter(filter) {
      const filter_cleaned = []
      filter.forEach((item) => {
        if (item.data_type == "String") {
          if (Array.isArray(item.value)) {
            item.value = String(item.value)
          }
          // let value = item.value.replace(/[']+/g, "")
          filter_cleaned.push({ ...item, value: item.value == null ? "" : item.value.trim() })
        } else {
          if (
            item.value == null ||
            item.value == undefined ||
            item.value == "" ||
            item.value == "null" ||
            item.value == "undefined" ||
            item.value == "None"
          ) {
            item.value = null
          } else if (
            (item.data_type == "Date" || item.data_type == "DateTime") &&
            !(
              item.operator.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DD")
          } else if (
            item.data_type == "DateTime" &&
            !(
              item.operator.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DDTHH:mm:ss")
          }
          filter_cleaned.push({ ...item, value: item.value })
        }
      })
      let parsed_filter = transformMetricFilterToServerFilter([...filter_cleaned])

      this.filter = parsed_filter.filter
      this.current_filter = parsed_filter.filter

      this.is_filter_valid = parsed_filter.valid
    },

    updateList(values) {
      const solid_metrics_calc = []
      const stat_metrics_calc = []
      this.metricsList.forEach((m) => {
        if (m.id === "s1") {
          solid_metrics_calc.push({ name: this.$t("Actual value"), id: "s1" })
        } else if (m.id === "s2") {
          solid_metrics_calc.push({ name: this.$t("Forecasted value"), id: "s2" })
        } else {
          stat_metrics_calc.push(m)
        }
      })

      if (this.group_type == "suggested") {
        this.$store.dispatch("data/updateSuggestedSolidMetrics", { id: values.id, list: solid_metrics_calc })
      } else {
        this.$store.dispatch("data/updateGrowthSolidMetrics", { id: values.id, list: solid_metrics_calc })
      }

      if (this.current_filter.length != 1) {
        dataService
          .updateMatrix(values.id, {
            displayed_metrics_stats: stat_metrics_calc.map((s) => s.id),
            name: values.name,
            description: values.description,
            is_public: true,
            filter: this.current_filter
          })
          .then((res) => {
            this.$toast.open({
              message:
                `${this.group_type == "suggested" ? this.$t("Suggested List") : this.$t("Growth Matrix")}` +
                " " +
                this.$t("updated successfully"),
              position: "top",
              type: "success",
              duration: 3000
            })
            this.$emit("done", res.data)
          })
          .catch(() => {
            this.$toast.open({
              message:
                this.$t("Error updating") +
                ` ${this.group_type == "suggested" ? this.$t("Suggested List") : this.$t("Growth Matrix")}, ` +
                this.$t("please try again!"),
              position: "top",
              type: "error",
              duration: 5000
            })
          })
      }
    }
  }
}
</script>
